import { ACCOUNT_ENDPOINTS } from "@/config/api"
import {
  SELECTED_CUSTOMER_ID_KEY,
  removeAuthItem,
  setAuthItem,
} from "@/lib/auth-storage"
import { httpClient } from "@/lib/http-client"

type JwtPayload = Record<string, unknown>

function decodeJwtPayload(token: string): JwtPayload | null {
  const parts = token.split(".")
  if (parts.length < 2) {
    return null
  }

  try {
    const base64 = parts[1].replace(/-/g, "+").replace(/_/g, "/")
    const padded = base64.padEnd(base64.length + ((4 - (base64.length % 4)) % 4), "=")
    const payload = JSON.parse(atob(padded))
    if (!payload || typeof payload !== "object" || Array.isArray(payload)) {
      return null
    }
    return payload as JwtPayload
  } catch {
    return null
  }
}

export function isMaskedCustomerToken(token: string | null | undefined): boolean {
  if (!token) {
    return false
  }

  const payload = decodeJwtPayload(token)
  if (!payload || payload.masked == null) {
    return false
  }

  return String(payload.masked).length > 0
}

function parseCustomerIdFromToken(token: string): number | null {
  const payload = decodeJwtPayload(token)
  const customerId = Number(payload?.sub)
  if (!Number.isFinite(customerId) || customerId <= 0) {
    return null
  }
  return customerId
}

function stripHashFromUrl(): void {
  const { pathname, search } = window.location
  window.history.replaceState(null, "", `${pathname}${search}`)
}

/**
 * Pure read of a pending one-time login code left in the URL hash by the
 * admin panel (`#otp=<code>`). Does not mutate the URL or touch the network
 * — safe to call more than once (e.g. React StrictMode's double-invoked
 * state initializers).
 */
export function peekOtpCodeFromHash(): string | null {
  const raw = window.location.hash.startsWith("#")
    ? window.location.hash.slice(1)
    : window.location.hash
  if (!raw) {
    return null
  }

  return new URLSearchParams(raw).get("otp")
}

export type OtpSession =
  | { kind: "customer"; token: string; customerId: number }
  | { kind: "account"; token: string }

/**
 * Consumes a one-time login code: strips it from the address bar and
 * redeems it at `POST /accounts/otp`. The code is single-use server-side
 * too, so callers must guarantee this runs at most once per code. An
 * impersonation code yields a masked customer session, an account code
 * (sign-in by QR) the account's own session.
 */
export async function exchangeOtp(code: string): Promise<OtpSession | null> {
  stripHashFromUrl()

  const response = await httpClient.post<{ token: string }>(
    ACCOUNT_ENDPOINTS.EXCHANGE_OTP,
    { code },
    false,
  )

  const token = response?.token
  if (!token) {
    return null
  }

  httpClient.clearTokens()
  removeAuthItem(SELECTED_CUSTOMER_ID_KEY)

  if (!isMaskedCustomerToken(token)) {
    httpClient.setAccountToken(token)
    return { kind: "account", token }
  }

  const customerId = parseCustomerIdFromToken(token)
  if (customerId == null) {
    return null
  }

  httpClient.setCustomerToken(token)
  setAuthItem(SELECTED_CUSTOMER_ID_KEY, String(customerId))

  return { kind: "customer", token, customerId }
}
